import {
  HttpClient,
  HttpErrorResponse,
  HttpEvent,
  HttpEventType,
  HttpParams,
  HttpProgressEvent,
  HttpResponse,
} from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { FilterDataImages } from '@core/models/filter-data-images.model';
import { HeatmapParams } from '@core/models/heatmap-params.model';
import { ImageTag } from '@core/models/image-tag.model';
import { Image } from '@core/models/image.model';
import { Resolution } from '@core/models/resolution.model';
import { SelectedArea } from '@core/models/selected-area.model';
import { SlideMetadata } from '@core/models/slide-metadata.model';
import { environment } from '@environment/environment';
import {
  BehaviorSubject,
  Observable,
  Subject,
  catchError,
  firstValueFrom,
  map,
  throwError,
} from 'rxjs';
import { FeatureToggleService } from '../feature-toggle-service/feature-toggle-service.service';

const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks

@Injectable({
  providedIn: 'root',
})
export class SlideService {
  currentSelectedArea = new Subject<SelectedArea>();

  inferenceOrder = new Subject<boolean>();
  captureOrder = new Subject<boolean>();
  activateHeatmap = new Subject<boolean>();

  heatmapActive = new BehaviorSubject<HeatmapParams>(new HeatmapParams());
  currentImage = new BehaviorSubject<Image>(new Image());
  progress = new BehaviorSubject<number>(0);
  filterData: FilterDataImages = new FilterDataImages();
  filterUpdateEmitter: EventEmitter<FilterDataImages> =
    new EventEmitter<FilterDataImages>();

  capturedImage = new Subject<string>();

  imageManualClassificationChanged = new Subject<Image>();

  cancerStructure: any = {
    LUNG: {
      subtypes: ['NON_APLICANT', 'ADENO', 'CEC', 'NORMAL'],
    },
    PROSTATE: {
      subtypes: ['NON_APLICANT', 'MALIGN', 'NORMAL'],
    },
  };

  externalUrl = '';

  constructor(
    private httpClient: HttpClient,
    private featureToggleService: FeatureToggleService,
  ) {
    this.featureToggleService.getConfig().subscribe((featureToggle) => {
      this.externalUrl = featureToggle.viewerUrlExternal;
    });
  }

  updateImageManualClassification(img: Image) {
    this.imageManualClassificationChanged.next(img);
  }

  getImageListPython(): any {
    const serviceUrl = `${environment.examInfoBackend}/slide/filter`;
    const params = new HttpParams({ fromObject: this.filterData.toObject() });
    return this.httpClient.get<any>(serviceUrl, {
      params,
      responseType: 'json',
    });
  }

  orderCapture(): void {
    this.captureOrder.next(true);
  }

  orderInference(): void {
    this.inferenceOrder.next(true);
  }

  updateSelectedArea(area: SelectedArea) {
    this.currentSelectedArea.next(area);
  }

  updateFilters(filter: FilterDataImages) {
    this.filterData = filter;
    this.filterUpdateEmitter.emit(this.filterData);
  }

  getResolutionAndDownloadImage(slideId: number): Observable<Resolution> {
    const url = `${environment.examInfoBackend}/slide/resolution/${slideId}`;
    return this.httpClient
      .get<Resolution>(url)
      .pipe(catchError((error) => throwError(() => error)));
  }

  setHeatmap(heatmapParams: HeatmapParams) {
    this.heatmapActive.next(heatmapParams);
  }

  setCurrentImage(image: Image) {
    this.currentImage.next(image);
  }

  setProgress(progress: number) {
    this.progress.next(progress);
  }

  saveProntuary(data: FormData): Observable<HttpEvent<any>> {
    const route = `${environment.examInfoBackend}/slide/upload`;
    return this.httpClient.post<HttpEvent<any>>(route, data, {
      reportProgress: true,
      observe: 'events',
    });
  }

  uploadSlide(file: File): Observable<HttpEvent<any>> {
    return new Observable((observer) => {
      let uploadId: string | null = null;
      let fileKey: string | null = null;

      const reportProgress = (percentage: number) => {
        observer.next({
          type: HttpEventType.UploadProgress,
          loaded: Math.floor((percentage / 100) * file.size),
          total: file.size,
        } as HttpProgressEvent);
      };

      this.initiateMultipartUpload(file)
        .then(async (initResult) => {
          uploadId = initResult.uploadId;
          fileKey = initResult.fileKey;

          try {
            let uploadedBytes = 0;
            const uploadPromises: Promise<{
              PartNumber: number;
              ETag: string;
            }>[] = [];
            const totalParts = Math.ceil(file.size / CHUNK_SIZE);
            let completedParts = 0;

            // Initial progress report
            reportProgress(1);

            // Create array of chunk upload promises
            for (let partNumber = 1; uploadedBytes < file.size; partNumber++) {
              const start = (partNumber - 1) * CHUNK_SIZE;
              const end = Math.min(start + CHUNK_SIZE, file.size);
              const chunk = file.slice(start, end);

              // Wrap the upload promise to track progress
              const uploadPromise = this.uploadPart(
                fileKey,
                uploadId,
                partNumber,
                chunk,
              ).then((result) => {
                completedParts++;
                // Report progress as parts complete (up to 90%)
                const partsProgress = (completedParts / totalParts) * 90;
                reportProgress(partsProgress);
                return result;
              });

              uploadPromises.push(uploadPromise);
              uploadedBytes += chunk.size;
            }

            // Upload all parts in parallel
            const parts = await Promise.all(uploadPromises);

            // Sort parts by part number to ensure correct order
            parts.sort((a, b) => a.PartNumber - b.PartNumber);

            // Report 90% progress - parts upload complete
            reportProgress(90);

            // Complete multipart upload
            await this.completeMultipartUpload(fileKey, uploadId, parts);

            // Report 95% progress - multipart upload completed
            reportProgress(95);

            // Call complete_upload endpoint
            const finalResult = await firstValueFrom(
              this.httpClient.post<any>(
                `${environment.examInfoBackend}/slide/complete_upload`,
                {
                  fileKey,
                  originalFileName: file.name,
                },
              ),
            );

            // Report 100% progress - all done
            reportProgress(100);

            observer.next(
              new HttpResponse({
                body: finalResult,
                status: 200,
                statusText: 'OK',
              }),
            );
            observer.complete();
          } catch (error) {
            observer.error(error);
          }
        })
        .catch((error) => {
          if (
            error instanceof HttpErrorResponse &&
            error.error.error === 'Use direct upload for local storage'
          ) {
            this.directUpload(file).subscribe(observer);
          } else {
            observer.error(error);
          }
        });

      // Clean up if the observer is unsubscribed or on error
      return async () => {
        if (uploadId && fileKey) {
          await this.abortMultipartUpload(fileKey, uploadId);
        }
      };
    });
  }

  private directUpload(file: File): Observable<HttpEvent<any>> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.httpClient
      .post<HttpEvent<any>>(
        `${environment.examInfoBackend}/slide/upload`,
        formData,
        {
          reportProgress: true,
          observe: 'events',
        },
      )
      .pipe(catchError((error) => throwError(() => error)));
  }

  private async initiateMultipartUpload(file: File) {
    const mimeType = this.getMimeType(file.name);
    const response = await firstValueFrom(
      this.httpClient.post<{ uploadId: string; fileKey: string }>(
        `${environment.examInfoBackend}/slide/presign_upload`,
        {
          fileName: file.name,
          fileType: mimeType,
        },
      ),
    );
    return response;
  }

  private getMimeType(filename: string): string {
    const ext = filename.toLowerCase().split('.').pop();
    switch (ext) {
      case 'svs':
        return 'application/x-svs';
      case 'tiff':
      case 'tif':
        return 'image/tiff';
      default:
        return 'application/octet-stream';
    }
  }

  private async uploadPart(
    fileKey: string,
    uploadId: string,
    partNumber: number,
    chunk: Blob,
  ): Promise<{ PartNumber: number; ETag: string }> {
    const { uploadUrl } = await this.getUploadPartUrl(
      fileKey,
      uploadId,
      partNumber,
    );

    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: chunk,
    });

    if (!response.ok) {
      throw new Error(`Failed to upload part ${partNumber}`);
    }

    const etag = response.headers.get('ETag');
    return {
      PartNumber: partNumber,
      ETag: etag!.replace(/['"]/g, ''),
    };
  }

  private async abortMultipartUpload(fileKey: string, uploadId: string) {
    try {
      await firstValueFrom(
        this.httpClient.post(
          `${environment.examInfoBackend}/slide/abort_multipart_upload`,
          {
            fileKey,
            uploadId,
          },
        ),
      );
    } catch (error) {
      console.error('Error aborting multipart upload:', error);
    }
  }

  private async getUploadPartUrl(
    fileKey: string,
    uploadId: string,
    partNumber: number,
  ) {
    const response = await firstValueFrom(
      this.httpClient.post<{ uploadUrl: string; partNumber: number }>(
        `${environment.examInfoBackend}/slide/presign_upload_part`,
        {
          fileKey,
          uploadId,
          partNumber,
        },
      ),
    );
    return response;
  }

  private async completeMultipartUpload(
    fileKey: string,
    uploadId: string,
    parts: { PartNumber: number; ETag: string }[],
  ) {
    const response = await firstValueFrom(
      this.httpClient.post<any>(
        `${environment.examInfoBackend}/slide/complete_multipart_upload`,
        {
          fileKey,
          uploadId,
          parts,
        },
      ),
    );
    return response;
  }

  getSlideMetadata(imageId: number): Observable<SlideMetadata> {
    const route = `${environment.examInfoBackend}/slide/metadata/${imageId}`;
    return this.httpClient
      .get<SlideMetadata>(route)
      .pipe(catchError((error) => throwError(error)));
  }

  updateSlideMetadata(imageId: number, data: any): Observable<Image> {
    const route = `${environment.examInfoBackend}/slide/metadata/${imageId}`;
    return this.httpClient
      .post<Image>(route, data)
      .pipe(catchError((error) => throwError(error)));
  }

  getDiagnosticImagesStatus(): Observable<
    { user: string; num_images: number }[]
  > {
    const route = `${environment.examInfoBackend}/slide/diagnostic_image_status`;
    return this.httpClient.get(route).pipe(
      map((x: any) => {
        return Object.keys(x).map((k) => {
          return {
            user: k,
            num_images: x[k],
          };
        });
      }),
    );
  }

  simpleClassifyDiagnostic(
    imageId: number,
    isDiagnostic: boolean,
    subtype: string,
  ): Observable<Image> {
    let route = '';
    if (isDiagnostic) {
      route = `${environment.examInfoBackend}/slide/simple_classify/${imageId}/diagnostic`;
    } else {
      route = `${environment.examInfoBackend}/slide/simple_classify/${imageId}/notdiagnostic`;
    }
    return this.httpClient.post<Image>(route, {
      subtype: subtype,
    });
  }

  getAvaliableTags(): Observable<ImageTag[]> {
    const route = `${environment.examInfoBackend}/exam_requests/image_tags`;
    return this.httpClient
      .get<ImageTag[]>(route)
      .pipe(catchError((error) => throwError(error)));
  }

  updateImageTags(imageId: number, data: any): Observable<Image> {
    const route = `${environment.examInfoBackend}/exam_requests/image_tags/${imageId}`;
    return this.httpClient
      .post<Image>(route, data)
      .pipe(catchError((error) => throwError(error)));
  }

  getImageTags(imageId: number): Observable<ImageTag[]> {
    const route = `${environment.examInfoBackend}/exam_requests/image_tags/${imageId}`;
    return this.httpClient
      .get<ImageTag[]>(route)
      .pipe(catchError((error) => throwError(error)));
  }

  getCancerStructure(): any {
    return this.cancerStructure;
  }

  captureCurrentView(): void {
    this.captureOrder.next(true);
  }

  uploadCapturedImage(base64Image: string): Observable<{ imageUrl: string }> {
    const url =
      'https://ioo43oe3ls42xfac4w2otjtoiu0befzl.lambda-url.us-east-1.on.aws/';
    const filename = 'captured_image_' + new Date().getTime();

    return new Observable((observer) => {
      fetch(url, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'text/plain',
        },
        body: JSON.stringify({ image: base64Image, filename: filename }),
      })
        .then((data) => {
          observer.next({
            imageUrl: `https://d3he1basyqweux.cloudfront.net/pdf_report_images/${filename}.png`,
          });
          observer.complete();
        })
        .catch((error) => {
          observer.error('Error uploading image');
        });
    });
  }

  getCurrentImageDzi(
    image?: Image | null,
    imageId?: number,
    externalUrl?: string,
  ) {
    // Use provided parameters or fall back to service state
    const targetImage = image || this.currentImage.value;
    const targetImageId = imageId || targetImage?.id;
    const targetExternalUrl = externalUrl || this.externalUrl;

    let dziUrl;
    const urlParams = new URLSearchParams();
    if (
      targetImage !== null &&
      targetImage !== undefined &&
      targetImage.num_revisions > 0
    ) {
      urlParams.set('rev', targetImage.num_revisions.toString());
    }
    const currentUrl = window.location.pathname;
    if (currentUrl.includes('/exam_open_access/')) {
      const queryParams = new URLSearchParams(window.location.search);
      const authkey = queryParams.get('auth_key');
      const giverId = queryParams.get('giver_id');
      const examId = currentUrl.match(/exam_open_access\/(\d+)/);
      if (authkey && giverId && examId) {
        urlParams.set('auth_key', authkey);
        urlParams.set('giver_id', giverId);
        urlParams.set('exam_request_id', examId[1]);
      }
    }
    const strParams = urlParams.toString();
    let getParams = '';
    if (strParams !== '') {
      getParams = '?' + urlParams.toString();
    }
    if (targetImage?.tiles_dzi) {
      if (targetImage.metadata?.use_tar_tiles) {
        if (targetImage.metadata?.permanent) {
          dziUrl = `${environment.lambdaMergedTilesPermanentApiUrl}${environment.environment}/${targetImageId}/dzi.dzi${getParams}`;
        } else if (targetImage.metadata?.use_dynamodb) {
          dziUrl = `${environment.lambdaMergedTilesApiUrl}${environment.environment}/${targetImageId}/dzi.dzi${getParams}`;
        } else {
          dziUrl = `${environment.lambdaMergedTilesApiUrl}${targetImageId}/dzi.dzi${getParams}`;
        }
      } else {
        dziUrl = `${targetImage.tiles_dzi}${getParams}`;
      }
    } else if (targetExternalUrl?.length) {
      dziUrl = `${targetExternalUrl}${targetImageId}/dzi`; // TODO: enable EFS url to use query params
    } else if (environment.useLambdaTiles) {
      dziUrl = `${environment.lambdaApiUrl}${targetImageId}/dzi.dzi${getParams}`;
    } else {
      if (environment.useTileGo) {
        dziUrl = `${environment.tilegoSlideManagerUrl}/slide/fetch_image/${targetImageId}.dzi${getParams}`;
      } else {
        dziUrl = `${environment.slideManagerUrl}/slide/fetch_image/${targetImageId}.dzi${getParams}`;
      }
    }
    return dziUrl;
  }
}
